import {
  GITHUB_BASE_URL,
  GITHUB_ORGANIZATION,
  GIT_REPO_NAMESPACE_POLICY,
  GIT_REPO_NAMESPACE_RULE,
  GIT_REPO_NAMESPACE_USER_FUNCTION,
} from '@/config'
import type { TableDataType } from '@/types'
import urlJoin from 'url-join'

const GITHUB_ORGANIZATION_BASE_URL =
  GITHUB_ORGANIZATION && GITHUB_BASE_URL ? urlJoin(GITHUB_BASE_URL, GITHUB_ORGANIZATION) : undefined

/**
 * Return a namespaced GitHub repository name.
 *
 * @param namespace Repository namespace (e.g., 'IPRP', 'IPRR', 'IPRF').
 * @param name Object name or id (e.g., `'my_rule'`).
 * @returns Namespaced repository name: `{namespace}-{name}`.
 */
function toNamespacedRepoName(namespace?: string, name?: string): string {
  return [namespace, name]
    .map(s => s?.trim())
    .filter(Boolean)
    .join('-')
}

/**
 * Build GitHub repository URL.
 *
 * @param namespace Repository namespace (e.g., 'IPRP', 'IPRR', 'IPRF').
 * @param name Object name or id (e.g., `'my_rule'`).
 * @returns Full GitHub URL: as `{GITHUB_BASE_URL}/{GITHUB_ORGANIZATION}/{namespace}-{name}`.
 */
function buildNamespacedGithubRepoUrl(namespace?: string, name?: string): string {
  const repo = toNamespacedRepoName(namespace, name)
  return urlJoin(GITHUB_ORGANIZATION_BASE_URL ?? '', repo)
}

export function buildGithubRuleRepoUrl(name?: string): string {
  return buildNamespacedGithubRepoUrl(GIT_REPO_NAMESPACE_RULE, name)
}

export function buildGithubPolicyRepoUrl(name?: string): string {
  return buildNamespacedGithubRepoUrl(GIT_REPO_NAMESPACE_POLICY, name)
}

export function buildGithubUserFunctionRepoUrl(name?: string): string {
  return buildNamespacedGithubRepoUrl(GIT_REPO_NAMESPACE_USER_FUNCTION, name)
}

/**
 * Build and return a GitHub repository HTTP(s) URL
 * for the specified resource.
 *
 * @param type One of `'rule'`, `'policy'` or `'function'`.
 * @param name The name of the rule, policy or user function.
 * @returns A full GitHub URL (or `undefined` if an unknown `type` is passed along).
 */
export function buildGithubRepoUrl(type: TableDataType, name: string) {
  switch (type) {
    case 'rule':
      return buildGithubRuleRepoUrl(name)
    case 'policy':
      return buildGithubPolicyRepoUrl(name)
    case 'function':
      return buildGithubUserFunctionRepoUrl(name)
    default:
      return undefined
  }
}

/**
 * Build and return a GitHub Git-ref compare URL.
 *
 * @param type One of `'rule'`, `'policy'` or `'function'`.
 * @param name The name of the rule, policy or user function.
 * @param source The branch or commit hash holding the changes to show.
 * @param target The branch or commit hash to compare against.
 * @returns A full GitHub compare URL: `{repoUrl}/compare/{target}...{source}`, which lists the changes in `source`
 *   that are not in `target`.
 */
export function buildGithubCompareUrl(type: TableDataType, name: string, source: string, target: string): string {
  const repoUrl = buildGithubRepoUrl(type, name)
  return `${repoUrl}/compare/${encodeURIComponent(target)}...${encodeURIComponent(source)}`
}
